import type { RubroRow } from './projectAutofillDraftUtils.ts'
import {
  captureRubroSlotAssignment,
  clearRubroSlotAssignment,
  restoreRubroSlotAssignment
} from './rubroSlotDeliverableUtils.ts'
import {
  metricSheetHasUsableRows,
  rubroBudgetLegajoInPdf,
  rubroHasMetricComputationPages,
  rubroMetricReadyFromRow
} from './projectAutofillWorkflow.ts'

export function rubroHasGeneratedBudgetSheet(rubro: RubroRow): boolean {
  return !!rubro.budgetSheetState
}

export function shouldWarnBeforeMetricRemoval(rubro: RubroRow): boolean {
  return rubroHasGeneratedBudgetSheet(rubro)
}

function cloneMetricWorkflowSnapshot(rubro: RubroRow): Partial<Omit<RubroRow, 'tempId'>> {
  const patch: Partial<Omit<RubroRow, 'tempId'>> = {}
  if (rubro.metricComputationView) {
    patch.metricComputationView = rubro.metricComputationView
  }
  if (rubro.metricLegajo) {
    patch.metricLegajo = JSON.parse(JSON.stringify(rubro.metricLegajo))
  }
  if (rubro.canImportMetricEdifySheet) {
    patch.canImportMetricEdifySheet = true
  }
  if (rubro.metricEligibility) {
    patch.metricEligibility = rubro.metricEligibility
  }
  return patch
}

export function captureMetricRemovalRestorePackage(
  rubro: RubroRow
): Partial<Omit<RubroRow, 'tempId'>> | null {
  const metricPart = captureRubroSlotAssignment(rubro, 'metricComputation')
  const patch: Partial<Omit<RubroRow, 'tempId'>> = {
    ...cloneMetricWorkflowSnapshot(rubro),
    ...(metricPart ?? {})
  }

  if (rubro.budgetSheetState && shouldClearBudgetWhenRemovingMetric(rubro)) {
    patch.budgetSheetState = JSON.parse(JSON.stringify(rubro.budgetSheetState))
  }

  return Object.keys(patch).length > 0 ? patch : null
}

export function shouldClearBudgetWhenRemovingMetric(rubro: RubroRow): boolean {
  return !!rubro.budgetSheetState
}

export function applyMetricRemovalWithBudgetCascade(
  rubro: RubroRow
): Partial<Omit<RubroRow, 'tempId'>> {
  const patch: Partial<Omit<RubroRow, 'tempId'>> = clearRubroSlotAssignment(rubro, 'metricComputation')
  patch.metricComputationView = 'missing'
  patch.metricLegajo = undefined
  patch.canImportMetricEdifySheet = undefined
  if (rubro.metricEligibility) {
    patch.metricEligibility = 'missing'
  }
  if (shouldClearBudgetWhenRemovingMetric(rubro)) {
    patch.budgetSheetState = undefined
  }
  return patch
}

export function restoreMetricRemovalPackage(
  rubro: RubroRow,
  snapshot: Partial<Omit<RubroRow, 'tempId'>>
): Partial<Omit<RubroRow, 'tempId'>> {
  const patch = restoreRubroSlotAssignment(rubro, snapshot)
  if (snapshot.metricComputationView !== undefined) {
    patch.metricComputationView = snapshot.metricComputationView
  }
  if ('metricLegajo' in snapshot) {
    patch.metricLegajo = snapshot.metricLegajo
  }
  if ('canImportMetricEdifySheet' in snapshot) {
    patch.canImportMetricEdifySheet = snapshot.canImportMetricEdifySheet
  }
  if (snapshot.metricEligibility !== undefined) {
    patch.metricEligibility = snapshot.metricEligibility
  }
  if ('metricSheetState' in snapshot) {
    patch.metricSheetState = snapshot.metricSheetState
  }
  if ('budgetSheetState' in snapshot) {
    patch.budgetSheetState = snapshot.budgetSheetState
  }
  return patch
}

export function showQuotationOrphanAfterMetricRemoval(
  rubro: RubroRow,
  metricRestoreSnapshot: Partial<Omit<RubroRow, 'tempId'>> | null | undefined
): boolean {
  if (!metricRestoreSnapshot) return false
  if (rubroMetricReadyFromRow(rubro)) return false
  if (rubro.budgetSheetState) return false
  if (rubroBudgetLegajoInPdf(rubro)) return false
  return 'budgetSheetState' in metricRestoreSnapshot && !!metricRestoreSnapshot.budgetSheetState
}

export function rubroMetricHasLocalSource(row: {
  documentationSlots?: { metricComputation?: number[] }
  metricSheetState?: { rows?: { cells?: Record<string, unknown> }[] } | null
}): boolean {
  return (
    rubroHasMetricComputationPages(row.documentationSlots ?? {}) ||
    metricSheetHasUsableRows(row.metricSheetState)
  )
}
